import {flatMap, uniq} from 'es-toolkit'
import invariant from 'invariant'
import {useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../hooks/redux'
import {fatalError} from '../redux/features/core'
import {
  openConfirmDeleteModal,
  openCreateInfillModal,
  openEditInfillsModal,
} from '../redux/features/modal'

import useAsync from '../../../commons/src/hooks/use-async'
import useSelectedIds from '../hooks/use-selected-ids'
import useSortAttributes, {SortDefinition} from '../hooks/use-sort-attributes'
import {formatFactorToPercentage, formatPrice, formatTimeRange} from '../libs/formatter'
import * as optionApi from '../services/api/option'
import {goToProviderInfos} from '../services/navigation'

import {OptionId, OptionWithLinkedProviderInfos} from '../types'

import deleteIcon from '../../../commons/src/assets/icons/delete.svg'
import editIcon from '../../../commons/src/assets/icons/edit.svg'
import tableSearchIcon from '../../../commons/src/assets/icons/table-search.svg'

import ActionBar from '../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../commons/src/components/button'
import CheckboxField from '../../../commons/src/components/checkbox-field'
import Headline from '../../../commons/src/components/headline'
import Icon from '../../../commons/src/components/icon'
import LinkButton from '../../../commons/src/components/link-button'
import Paragraph from '../../../commons/src/components/paragraph'
import ActionHeaderLayout from '../components/action-header-layout'
import ActionHeader from '../components/action-header-layout/action-header'
import Container from '../components/container'
import PageContentLayout from '../components/page-content-layout'
import PageHeader from '../components/page-content-layout/PageHeader'
import Table from '../components/table'
import TableCell from '../components/table/TableCell'
import TableHeaderCell from '../components/table/TableHeaderCell'
import TableRow from '../components/table/TableRow'

import PagePartial from './page-partial'

type TableRowContent = OptionWithLinkedProviderInfos & {
  name: string
  infill: number
  basePrice: number
  factor: number
  productionTimeFastOffset: number
  productionTimeSlowOffset: number
}

const SORT_ATTRIBUTES: SortDefinition<TableRowContent> = {
  name: {
    asc: ({name: a}, {name: b}) => a.localeCompare(b),
    desc: ({name: a}, {name: b}) => b.localeCompare(a),
  },
  infill: {
    asc: ({infill: a}, {infill: b}) => a - b,
    desc: ({infill: a}, {infill: b}) => b - a,
  },
  basePrice: {
    asc: ({basePrice: a}, {basePrice: b}) => a - b,
    desc: ({basePrice: a}, {basePrice: b}) => b - a,
  },
  productionTime: {
    asc: ({productionTimeSlowOffset: a}, {productionTimeFastOffset: b}) => a - b,
    desc: ({productionTimeFastOffset: a}, {productionTimeSlowOffset: b}) => b - a,
  },
  factor: {
    asc: ({factor: a}, {factor: b}) => a - b,
    desc: ({factor: a}, {factor: b}) => b - a,
  },
  linkedConfigs: {
    asc: ({providerInfos: a}, {providerInfos: b}) => a.length - b.length,
    desc: ({providerInfos: a}, {providerInfos: b}) => b.length - a.length,
  },
}

const getNumberOfLinkedConfigs = (
  infills: OptionWithLinkedProviderInfos[],
  selectedIds: OptionId[]
) => {
  const selectedInfills = infills.filter(({id}) => selectedIds.includes(id))
  const uniqueProviderInfoIds = uniq(
    flatMap(selectedInfills, infill => infill.providerInfos.map(providerInfo => providerInfo.id))
  )

  return uniqueProviderInfoIds.length
}

const InfillsPage = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const {
    isLoading,
    data: infills = [],
    refresh,
  } = useAsync(() => optionApi.getInfillsWithLinkedProviderInfosByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
    defaultLoading: true,
  })

  const {toggleId, toggleAll, removeIds, isSelected, isAnySelected, selectedIds} =
    useSelectedIds<OptionId>(() => infills.map(infill => infill.id))

  const refreshAndDeselect = () => {
    refresh()
    removeIds(selectedIds)
  }

  const tableRows: TableRowContent[] = infills.map(infill => ({
    ...infill,
    name: infill.name || '-',
    infill: parseInt(infill.type.replace('infill_', ''), 10),
    basePrice: infill.basePrice || 0,
    factor: infill.factor || 0,
    productionTimeFastOffset: infill.productionTimeFastOffset || 0,
    productionTimeSlowOffset: infill.productionTimeSlowOffset || 0,
  }))

  const {
    sortedItems: sortedTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(tableRows, SORT_ATTRIBUTES, 'name')

  const renderRow = (tableRow: TableRowContent) => (
    <TableRow key={tableRow.id}>
      <TableCell>
        <ActionBar>
          <CheckboxField checked={isSelected(tableRow.id)} onChange={() => toggleId(tableRow.id)} />
        </ActionBar>
      </TableCell>
      <TableCell strong>{tableRow.name}</TableCell>
      <TableCell>{tableRow.infill}%</TableCell>
      <TableCell>{tableRow.factor ? formatFactorToPercentage(tableRow.factor) : '-'}</TableCell>
      <TableCell>
        {tableRow.basePrice === 0 ? '-' : formatPrice(tableRow.basePrice, provider.currency)}
      </TableCell>
      <TableCell>
        {formatTimeRange(tableRow.productionTimeFastOffset, tableRow.productionTimeSlowOffset)}
      </TableCell>
      <TableCell error={tableRow.providerInfos.length === 0}>
        {tableRow.providerInfos.length > 0 ? (
          <LinkButton
            icon={<Icon source={tableSearchIcon} />}
            iconAlign="right"
            onClick={() =>
              goToProviderInfos(navigate, {
                initialFilters: {infill: [tableRow.id]},
              })
            }
          >
            <strong>{tableRow.providerInfos.length}</strong>
          </LinkButton>
        ) : (
          <strong>{tableRow.providerInfos.length}</strong>
        )}
      </TableCell>
    </TableRow>
  )

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={<Headline size="l" label="Infill options" className="u-no-margin-bottom" />}
            actions={
              <Button onClick={() => dispatch(openCreateInfillModal({onDone: refreshAndDeselect}))}>
                Create new option
              </Button>
            }
          >
            <Paragraph size="s" minor className="u-no-margin-bottom">
              Infills are available for all <strong>FDM</strong> printing materials.
            </Paragraph>
          </PageHeader>
        }
      >
        <Container>
          <ActionHeaderLayout
            header={
              <ActionHeader
                left={
                  <LinkButton onClick={() => toggleAll()}>
                    {isAnySelected() ? 'Deselect all' : 'Select all'}
                  </LinkButton>
                }
                right={
                  <>
                    <LinkButton
                      size="200"
                      icon={<Icon source={editIcon} title="Edit" />}
                      disabled={selectedIds.length === 0}
                      onClick={() =>
                        dispatch(
                          openEditInfillsModal({
                            infills: infills.filter(({id}) => selectedIds.includes(id)),
                            onDone: refreshAndDeselect,
                          })
                        )
                      }
                    />
                    <LinkButton
                      size="200"
                      icon={<Icon source={deleteIcon} title="Delete" />}
                      disabled={selectedIds.length === 0}
                      onClick={() => {
                        dispatch(
                          openConfirmDeleteModal({
                            itemsToDeleteCount: selectedIds.length,
                            onConfirm: () => {
                              optionApi.removeMultiple(selectedIds).then(refresh).catch(fatalError)
                            },
                            linkedConfigsCount: getNumberOfLinkedConfigs(infills, selectedIds),
                          })
                        )
                      }}
                    />
                  </>
                }
              />
            }
          >
            <Table
              isLoading={isLoading}
              empty="No infills have been added yet."
              header={
                <TableRow>
                  <TableHeaderCell width="28px" />
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'name' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('name')}
                  >
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    sort={sortAttribute === 'infill' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('infill')}
                  >
                    Infill
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    align="right"
                    sort={sortAttribute === 'factor' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('factor')}
                  >
                    Additional production cost
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    align="right"
                    sort={sortAttribute === 'basePrice' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('basePrice')}
                  >
                    Base price
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    align="right"
                    sort={sortAttribute === 'productionTime' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('productionTime')}
                  >
                    Additional production time
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={4}
                    align="right"
                    sort={sortAttribute === 'linkedConfigs' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('linkedConfigs')}
                  >
                    Linked materials
                  </TableHeaderCell>
                </TableRow>
              }
            >
              {sortedTableRows.map(renderRow)}
            </Table>
          </ActionHeaderLayout>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default InfillsPage
